import {
  Position,
  PlayerFacing,
  BuildingType,
  BeltType,
  BELT_CHARACTERS,
  FAST_BELT_CHARACTERS,
} from './constants';
import { BuildingLayer } from './GameState';

// Belt placement tracking interface
export interface BeltPlacement {
  position: Position;
  beltType: BeltType;
  direction: PlayerFacing;
  timestamp: number;
}

// Smart belt placement state
interface SmartBeltState {
  lastBeltPlacement: BeltPlacement | null;
  beltPlacements: BeltPlacement[];
}

// Global state for smart belt system
let smartBeltState: SmartBeltState = {
  lastBeltPlacement: null,
  beltPlacements: [],
};

// Get the character for a belt direction and type
export const getBeltCharacterByDirection = (
  beltType: BeltType,
  direction: PlayerFacing
): string => {
  if (beltType === BuildingType.BELT) {
    return BELT_CHARACTERS[direction];
  } else {
    return FAST_BELT_CHARACTERS[direction];
  }
};

// Get belt direction from character at a specific position
export const getBeltDirectionFromCharacter = (
  buildingLayer: BuildingLayer,
  position: Position
): PlayerFacing | null => {
  const key = `${position.x},${position.y}`;
  const character = buildingLayer[key];

  if (!character) {
    return null;
  }

  // Check regular belts
  for (const [direction, char] of Object.entries(BELT_CHARACTERS)) {
    if (char === character) {
      return direction as PlayerFacing;
    }
  }

  // Check fast belts
  for (const [direction, char] of Object.entries(FAST_BELT_CHARACTERS)) {
    if (char === character) {
      return direction as PlayerFacing;
    }
  }

  return null;
};

// Get belt type from character
export const getBeltTypeFromCharacter = (
  character: string
): BeltType | null => {
  // Check if it's a regular belt
  if ((Object.values(BELT_CHARACTERS) as string[]).includes(character)) {
    return BuildingType.BELT;
  }

  // Check if it's a fast belt
  if ((Object.values(FAST_BELT_CHARACTERS) as string[]).includes(character)) {
    return BuildingType.FAST_BELT;
  }

  return null;
};

// Calculate direction from source to target position
export const getDirectionBetweenPositions = (
  from: Position,
  to: Position
): PlayerFacing | null => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;

  // Only handle adjacent positions (1 tile away)
  if (Math.abs(dx) + Math.abs(dy) !== 1) {
    return null;
  }

  if (dx === 1) return 'right';
  if (dx === -1) return 'left';
  if (dy === 1) return 'down';
  if (dy === -1) return 'up';

  return null;
};

// Check if two positions are adjacent (N, E, W, S)
export const arePositionsAdjacent = (
  pos1: Position,
  pos2: Position
): boolean => {
  const dx = Math.abs(pos1.x - pos2.x);
  const dy = Math.abs(pos1.y - pos2.y);
  return (dx === 1 && dy === 0) || (dx === 0 && dy === 1);
};

// Record a new belt placement
export const recordBeltPlacement = (
  position: Position,
  beltType: BeltType,
  direction: PlayerFacing
): void => {
  const placement: BeltPlacement = {
    position,
    beltType,
    direction,
    timestamp: Date.now(),
  };

  // Update last placement
  smartBeltState.lastBeltPlacement = placement;

  // Add to placement history
  smartBeltState.beltPlacements.push(placement);

  // Keep only last 50 placements to prevent memory bloat
  if (smartBeltState.beltPlacements.length > 50) {
    smartBeltState.beltPlacements = smartBeltState.beltPlacements.slice(-50);
  }
};

// Get the last belt placement
export const getLastBeltPlacement = (): BeltPlacement | null => {
  return smartBeltState.lastBeltPlacement;
};

// Smart belt placement logic
export const processSmartBeltPlacement = (
  newPosition: Position,
  newBeltType: BeltType,
  newDirection: PlayerFacing,
  buildingLayer: BuildingLayer
): {
  updatedBuildingLayer: BuildingLayer;
  previousBeltUpdated: boolean;
  previousBeltPosition?: Position | undefined;
  previousBeltOldDirection?: PlayerFacing | undefined;
  previousBeltNewDirection?: PlayerFacing | undefined;
  optimizedNewDirection?: PlayerFacing | undefined;
} => {
  const lastPlacement = getLastBeltPlacement();
  const updatedBuildingLayer = { ...buildingLayer };
  let previousBeltUpdated = false;
  let previousBeltPosition: Position | undefined;
  let previousBeltOldDirection: PlayerFacing | undefined;
  let previousBeltNewDirection: PlayerFacing | undefined;
  let optimizedNewDirection: PlayerFacing | undefined;

  // If we have a last belt placement and the new belt is adjacent
  if (
    lastPlacement &&
    arePositionsAdjacent(lastPlacement.position, newPosition)
  ) {
    // Calculate what direction the previous belt should face to connect to the new belt
    const connectionDirection = getDirectionBetweenPositions(
      lastPlacement.position,
      newPosition
    );

    if (connectionDirection) {
      // For flow continuity, the new belt should continue in the same direction
      // as the connection between the previous and new belt positions
      optimizedNewDirection = connectionDirection;

      // Update the previous belt's direction if needed
      if (connectionDirection !== lastPlacement.direction) {
        // Update the previous belt's direction to connect to the new belt
        const previousKey = `${lastPlacement.position.x},${lastPlacement.position.y}`;
        const currentBeltChar = buildingLayer[previousKey];

        // Verify it's still a belt and matches our tracking
        if (currentBeltChar) {
          const currentBeltType = getBeltTypeFromCharacter(currentBeltChar);
          const currentDirection = getBeltDirectionFromCharacter(
            buildingLayer,
            lastPlacement.position
          );

          if (
            currentBeltType === lastPlacement.beltType &&
            currentDirection === lastPlacement.direction
          ) {
            // Update the belt direction
            const newBeltChar = getBeltCharacterByDirection(
              lastPlacement.beltType,
              connectionDirection
            );
            updatedBuildingLayer[previousKey] = newBeltChar;

            // Update our tracking
            smartBeltState.lastBeltPlacement = {
              ...lastPlacement,
              direction: connectionDirection,
            };

            previousBeltUpdated = true;
            previousBeltPosition = lastPlacement.position;
            previousBeltOldDirection = lastPlacement.direction;
            previousBeltNewDirection = connectionDirection;
          }
        }
      }
    }
  }

  // Record the new belt placement with the optimized direction if available
  const finalDirection = optimizedNewDirection || newDirection;
  recordBeltPlacement(newPosition, newBeltType, finalDirection);

  return {
    updatedBuildingLayer,
    previousBeltUpdated,
    previousBeltPosition,
    previousBeltOldDirection,
    previousBeltNewDirection,
    optimizedNewDirection,
  };
};

// Clear belt placement history (useful for testing or game reset)
export const clearBeltPlacementHistory = (): void => {
  smartBeltState = {
    lastBeltPlacement: null,
    beltPlacements: [],
  };
};

// Get all belt placements (for debugging)
export const getAllBeltPlacements = (): BeltPlacement[] => {
  return [...smartBeltState.beltPlacements];
};
